import { T, Var } from 'gt-react';
import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type {
  FamousVoucher,
  FamousVoucherFile,
  FamousVoucherFileRef,
  FamousVoucherLine,
} from '@erp/rpc';
import { famousFileKey } from '../shared/famousVoucherCoverage';

export type FormatMoney = (amount: string) => string;

/** "Load 10258", "Order 127954", "PO 24268". */
export function FamousFileLabel({ file }: { file: FamousVoucherFileRef }) {
  const number = file.fileNumber || '—';
  switch (file.fileType) {
    case 'load':
      return (
        <T context="Famous sales load, followed by its number">
          Load <Var>{number}</Var>
        </T>
      );
    case 'order':
      return (
        <T context="Famous sales order, followed by its number">
          Order <Var>{number}</Var>
        </T>
      );
    case 'po':
      return (
        <T context="Famous purchase order, followed by its number">
          PO <Var>{number}</Var>
        </T>
      );
    default:
      return (
        <T context="voucher lines that name no Famous load, order or PO">
          Other lines
        </T>
      );
  }
}

function FileList({ files }: { files: FamousVoucherFileRef[] }) {
  return (
    <>
      {files.map((file, index) => (
        <span key={famousFileKey(file)}>
          {index > 0 && ', '}
          <FamousFileLabel file={file} />
        </span>
      ))}
    </>
  );
}

function VoucherStateBadges({ voucher }: { voucher: FamousVoucher }) {
  return (
    <>
      {voucher.isCurrent && (
        <Badge className="text-[10px] h-4">
          <T context="the Famous voucher that counts for the invoice">
            Current
          </T>
        </Badge>
      )}
      <Badge variant="outline" className="text-[10px] h-4">
        {voucher.state === 'paid' ? (
          <T context="Famous voucher status">Paid</T>
        ) : voucher.state === 'unvouchered' ? (
          <T context="Famous voucher voided to $0">Unvouchered</T>
        ) : (
          <T context="Famous voucher status">Open</T>
        )}
      </Badge>
      {voucher.onHold && (
        <Badge variant="outline" className="text-[10px] h-4">
          <T context="Famous voucher is on hold">On hold</T>
        </Badge>
      )}
    </>
  );
}

function VoucherCoverageNotes({
  voucher,
  invoiceTotal,
  money,
}: {
  voucher: FamousVoucher;
  invoiceTotal: string | null;
  money: FormatMoney;
}) {
  const coverage = voucher.coverage;
  if (!coverage) return null;
  return (
    <div className="space-y-1 text-xs">
      {coverage.complete ? (
        <div className="flex items-center gap-1.5 text-green-700 dark:text-green-400">
          <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" />
          <T context="Famous voucher has a line on every load, order and PO of the invoice">
            Covers every file on this invoice (
            <Var>{coverage.covered.length}</Var>)
          </T>
        </div>
      ) : (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <WarningIcon className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>
            <T context="Famous voucher has no line on these loads, orders or POs">
              Does not cover: <FileList files={coverage.missing} />
            </T>
          </span>
        </div>
      )}
      {coverage.notLinked.length > 0 && (
        <div className="text-muted-foreground">
          <T context="Famous voucher has lines on files the invoice isn't linked to in Gently">
            Includes <FileList files={coverage.notLinked} />, not linked to this
            invoice in Gently.
          </T>
        </div>
      )}
      {voucher.amountDiffersFromInvoice && invoiceTotal !== null && (
        <div className="flex items-center gap-1.5 text-red-600">
          <WarningIcon className="h-3.5 w-3.5 shrink-0" />
          <T context="Famous voucher amount is not the invoice total">
            Voucher total differs from the invoice total (
            <Var>{money(invoiceTotal)}</Var>)
          </T>
        </div>
      )}
    </div>
  );
}

function VoucherFileRows({
  file,
  lines,
  money,
}: {
  file: FamousVoucherFile;
  lines: FamousVoucherLine[];
  money: FormatMoney;
}) {
  const key = famousFileKey(file);
  return (
    <>
      <TableRow className="bg-muted/40 hover:bg-muted/40">
        <TableCell className="font-medium text-xs">
          <FamousFileLabel file={file} />
        </TableCell>
        <TableCell className="text-right font-medium tabular-nums text-xs">
          {money(file.subtotal)}
        </TableCell>
      </TableRow>
      {lines.flatMap((line) =>
        famousFileKey({
          fileType: line.fileType,
          fileNumber: line.fileNumber ?? '',
        }) === key
          ? [
              // A line's sequence is unique within its voucher, so it keys
              // the row without depending on list order.
              <TableRow key={`${key}:${line.lineSeq}:${line.description}`}>
                <TableCell className="pl-6 text-xs">
                  {line.description ?? '—'}
                </TableCell>
                <TableCell
                  className={cn(
                    'text-right tabular-nums text-xs',
                    line.amount.startsWith('-') && 'text-red-600',
                  )}
                >
                  {money(line.amount)}
                </TableCell>
              </TableRow>,
            ]
          : [],
      )}
    </>
  );
}

/**
 * One Famous voucher: number, status, amount and stub, whether it covers
 * every file on the invoice, and its lines grouped by load / order / PO.
 */
export function FamousVoucherCard({
  voucher,
  invoiceTotal,
  money,
}: {
  voucher: FamousVoucher;
  invoiceTotal: string | null;
  money: FormatMoney;
}) {
  return (
    <div
      className={cn(
        'rounded-md border bg-card',
        voucher.isCurrent && 'border-primary/40',
      )}
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b">
        <span className="text-sm font-medium">
          <T>
            Voucher <Var>{voucher.voucherNumber}</Var>
          </T>
        </span>
        <VoucherStateBadges voucher={voucher} />
        <span className="ml-auto text-sm font-medium tabular-nums">
          {money(voucher.amount)}
        </span>
      </div>
      <div className="space-y-2 px-3 py-2">
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          {voucher.stubDescription && (
            <span
              title={
                voucher.apStatus ? `AP status ${voucher.apStatus}` : undefined
              }
            >
              <T context="Famous voucher stub description, shown next to its text">
                Stub: <Var>{voucher.stubDescription}</Var>
              </T>
            </span>
          )}
          <span>
            <T context="Famous voucher's invoice number as keyed in Famous">
              Invoice <Var>{voucher.invoiceNumber}</Var>
            </T>
          </span>
        </div>
        <VoucherCoverageNotes
          voucher={voucher}
          invoiceTotal={invoiceTotal}
          money={money}
        />
        {voucher.files.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">
                  <T>Line</T>
                </TableHead>
                <TableHead className="text-right text-xs">
                  <T>Amount</T>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {voucher.files.map((file) => (
                <VoucherFileRows
                  key={famousFileKey(file)}
                  file={file}
                  lines={voucher.lines}
                  money={money}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
